import { DndContext, DragOverlay } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  ConfigFormItem,
  getWidgetProps,
  RichTextAdvancedRender,
  useBasicDndDragger,
  type IWidgetComponentProps,
} from '@framework/engine';
import { useRelationEditor } from '@framework/form';
import { useTranslation } from '@framework/i18n';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { MultiPreferenceRow } from './components/MultiPreferenceRow';
import { MultiPreferenceRowLabels } from './components/MultiPreferenceRowLabels';
import { MultiPreferenceSortableRow } from './components/MultiPreferenceSortableRow';
import { type IMultiPreferenceRow } from './createMultiPreferenceFormSchema';
import { type IMultiPreferenceWidget } from './IMultiPreferenceWidget';
import { MultiPreferenceWidgetButton } from './MultiPreferenceWidgetButton';

const MultiPreferenceBorderWrapper = styled.div`
  border: 1px solid ${props => props.theme.colorBorder};
  border-radius: ${props => props.theme.borderRadius}px;
  padding: 12px;
  // Errori
  .ant-form-item-has-error & {
    border-color: ${props => props.theme.colorError};
  }
`;

export interface IMultiPreferenceWidgetComponentProps
  extends IWidgetComponentProps<IMultiPreferenceWidget> {}

/**
 * Widget lato form (compilazione) per la selezione delle preferenze
 * multiple.
 * Permette di selezionare le preferenze da una lista di opzioni
 * definite in fase di configurazione.
 * Le preferenze sono ordinabili tramite drag&drop.
 */
export function MultiPreferenceWidgetComponent(
  props: IMultiPreferenceWidgetComponentProps,
) {
  const { widget } = props;
  const options = getWidgetProps(props);
  const editor = useRelationEditor<IMultiPreferenceRow>(options.name);
  const [isResetPending, setIsResetPending] = useState(false);

  const { sensors, handleDragStart, handleDragEnd, itemIds, active } =
    useBasicDndDragger(editor);

  const { t } = useTranslation();

  const repeatRule = widget.rules?.repeatableRange;

  const itemCount = editor.items.length;
  const isMax = useMemo(
    () => repeatRule?.max && repeatRule.max <= itemCount,
    [itemCount, repeatRule],
  );

  useEffect(() => {
    if (!isResetPending) return;
    if (editor.items.length > 0) editor.replace([]);

    setIsResetPending(false);
  }, [isResetPending, editor]);

  return (
    <ConfigFormItem
      name={options.name}
      label={<RichTextAdvancedRender text={options.label} />}
      help={<RichTextAdvancedRender text={options.helpText} />}
      onlyFirstLevelErrors
      layout={{
        hideVisualLabel: options.hideLabel,
      }}
    >
      <MultiPreferenceBorderWrapper>
        {editor.items.length > 0 && (
          <MultiPreferenceRowLabels widget={widget} />
        )}
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext
            items={itemIds}
            strategy={verticalListSortingStrategy}
            disabled={options.readonly}
          >
            {editor.items.map((item, i) => (
              <MultiPreferenceSortableRow
                id={item.__uid!}
                key={item.__uid}
                index={i}
                name={options.name}
                widget={widget}
                options={options}
                configMode
                onRemove={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  editor.removeAt(i);
                }}
              />
            ))}

            <DragOverlay>
              {active && (
                <MultiPreferenceRow
                  index={editor.items.findIndex(p => p.__uid === active.__uid)}
                  draggingItem={active}
                  name={options.name}
                  widget={widget}
                  options={options}
                  isDragging
                />
              )}
            </DragOverlay>
          </SortableContext>
        </DndContext>
        {!options.readonly && (
          <MultiPreferenceWidgetButton
            editor={editor}
            isMax={!!isMax}
            onReset={() => setIsResetPending(true)}
          />
        )}
      </MultiPreferenceBorderWrapper>
    </ConfigFormItem>
  );
}
